import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { 
	faGraduationCap, 
	faBriefcase, 
	faChartLine,
} from "@fortawesome/free-solid-svg-icons";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";

import INFO from "../data/user";
import SEO from "../data/seo";

import "./styles/about.css";

// Horizontal Timeline Component
const HorizontalTimeline = () => {
	const timelineData = [
			{
			year: "2020-2024", 
			title: "University of Pittsburgh",
			description: "Henry: B.S. Economics-Statistics (Departmental Honors, Multiple Awards)",
			type: "education"
		},
		{
			year: "2020-2025",
			title: "University of Pittsburgh",
			description: "Logan: B.S. & M.A. - Statistics & Applied Statistics (Departmental Honors)",
			type: "education"
		},
		{
			year: "2024",
			title: "Fortune 100 Tech Company",
			description: "Henry: Federal Data Scientist - AI/NLP for Govt. Agencies",
			type: "work"
		},
		{
			year: "2025",
			title: "Leading Financial Institution",
			description: "Logan: AI Data Scientist - Developing data driven sales tools",
			type: "work"
		},
		{
			year: "2025",
			title: "SB Statistical Consulting",
			description: "Founded SB Statistical Consulting where we focus on the statistics, so you can focus on what you love",
			type: "sb"
		}
	];

	return (
		<div className="horizontal-timeline-container">
			<div className="horizontal-timeline-line"></div>
			<div className="horizontal-timeline-items">
				{timelineData.map((item, index) => (
					<div key={index} className={`horizontal-timeline-item ${item.type}`}>
						<div className="horizontal-timeline-marker">
							<FontAwesomeIcon 
								icon={
									item.type === 'education' ? faGraduationCap :
									item.type === 'work' ? faBriefcase :
									faChartLine
								} 
							/>
						</div>
						<div className="horizontal-timeline-content">
							<div className="horizontal-timeline-year">{item.year}</div>
							<div className="horizontal-timeline-title">{item.title}</div>
							<div className="horizontal-timeline-description">{item.description}</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

const About = () => {
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const currentSEO = SEO.find((item) => item.page === "about");

	return (
		<React.Fragment>
			<Helmet>
				<title>{`Our Team | ${INFO.main.title}`}</title>
				<meta name="description" content={currentSEO.description} />
				<meta
					name="keywords"
					content={currentSEO.keywords.join(", ")}
				/>
			</Helmet>

			<div className="page-content">
				<NavBar active="about" />
				<div className="content-wrapper">
					<div className="about-logo-container">
						<div className="about-logo">
							<Logo width={46} />
						</div>
					</div>

					<div className="about-container">
						<div className="about-main">
							<div className="about-header">
								<div className="title about-title">
									Meet Our Founding Partners
								</div>
								<div className="subtitle about-subtitle">
									Combining expertise in data science, statistics, and business optimization to deliver impactful statistical consulting solutions.
								</div>
							</div>

							{/* Logan Section */}
							<div className="partner-section">
								<div className="partner-content">
									<div className="partner-title">Logan Schafer - Founding Partner</div>
									<div className="partner-description">
										Logan is an AI data scientist at one of America's largest financial institutions, where he develops data driven tools to enhance sales performance and decision-making. He holds both a bachelor's and master's degree in statistics from the University of Pittsburgh, graduating with departmental honors. Logan has also co-authored a research paper that is currently under review for publication in an academic journal, further reflecting his strong background in applied research. In his free time, he enjoys weightlifting, playing basketball, and eating impressive amounts of donuts.
									</div>
								</div>
								<div className="partner-image-section">
									<div className="partner-image-container">
										<div className="partner-image-wrapper">
											<img
												src="logan_headshot.png"
												alt="Logan Schafer"
												className="partner-image"
											/>
										</div>
									</div>
									<div className="partner-socials">
										<a
											href="https://www.linkedin.com/in/logan-schafer/"
											target="_blank"
											rel="noreferrer"
											className="partner-social-link"
										>
											<FontAwesomeIcon icon={faLinkedin} className="partner-social-icon" />
										</a>
										<a
											href="mailto:lschafer140@gmail.com"
											className="partner-social-link"
										>
											<FontAwesomeIcon icon={faEnvelope} className="partner-social-icon" />
										</a>
									</div>
								</div>
							</div>

							{/* Henry Section */}
							<div className="partner-section">
								<div className="partner-content">
									<div className="partner-title">Henry Bump - Founding Partner</div>
									<div className="partner-description">
										Henry is a Federal Data Scientist at a Fortune 100 tech company, where he develops AI and NLP solutions for Government Agencies. He graduated with departmental honors from the University of Pittsburgh with a degree in Economics-Statistics, earning multiple awards including the Pitt Economics Outstanding Senior and Statistics Department UTA of the Year awards. Henry's expertise spans consulting, financial analytics, and statistical analysis through his industry and academic experience. In his free time, he enjoys mountain biking, skiing, and playing soccer.
									</div>
								</div>
								<div className="partner-image-section">
									<div className="partner-image-container">
										<div className="partner-image-wrapper">
											<img
												src="henry_headshot.png"
												alt="Henry Bump"
												className="partner-image"
											/>
										</div>
									</div>
									<div className="partner-socials">
										<a
											href="https://www.linkedin.com/in/henry-bump-3128231a2/"
											target="_blank"
											rel="noreferrer"
											className="partner-social-link"
										>
											<FontAwesomeIcon icon={faLinkedin} className="partner-social-icon" />
										</a>
										<a
											href="mailto:hbump35@gmail.com"
											className="partner-social-link"
										>
											<FontAwesomeIcon icon={faEnvelope} className="partner-social-icon" />
										</a>
									</div>
								</div>
							</div>

							{/* Our Story Section with Horizontal Timeline */}
							<div className="our-story-section">
								<div className="our-story-header">
									<h2 className="our-story-title">Our Story</h2>
								</div>
								<HorizontalTimeline />
							</div>
						</div>
					</div>
					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default About;